import { Component, type ErrorInfo, type ReactNode } from "react";

import i18next from "i18next";

/**
 * 전역 에러 바운더리 — 렌더 중 예외로 화면이 빈 채로 잠기는 것을 방지함.
 *
 * 어떤 페이지가 크래시해도 앱 전체가 빈 화면이 되어 로그아웃/이동조차 못하는 상태를 막기 위해,
 * 복구 UI(홈으로 / 로그아웃)를 제공함. "로그아웃"은 하드 내비게이션으로 앱을 리로드해
 * 인메모리 세션(토큰·스토어)을 초기화한 뒤 /login 으로 보냄.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 개발 편의 로깅(운영은 관측 도구로 대체 가능).
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private goHome = (): void => {
    // 하드 내비게이션 → 앱 리로드로 손상된 렌더 트리를 폐기하고 부트스트랩 재실행.
    window.location.assign("/workspaces");
  };

  private logout = (): void => {
    // 하드 리로드가 인메모리 토큰/스토어를 초기화 → 미인증 → /login.
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      /* 스토리지 접근 불가 시 무시 */
    }
    window.location.assign("/login");
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--bg, #f7f8fa)",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "28px 26px",
            textAlign: "center",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
            {i18next.t("app.error.title")}
          </div>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.6 }}>
            {i18next.t("app.error.hint")}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              type="button"
              onClick={this.goHome}
              style={{
                padding: "9px 18px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {i18next.t("app.error.goHome")}
            </button>
            <button
              type="button"
              onClick={this.logout}
              style={{
                padding: "9px 18px",
                borderRadius: 8,
                border: "none",
                background: "#4f46e5",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {i18next.t("auth.logout")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
